import { InlineIcon } from "@iconify/react/dist/iconify.js";
import { useMutation, useQuery } from "@tanstack/react-query";
import _ from "lodash";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  CHAT_ATTACHMENT_MIME_TYPES,
  createQuotationChat,
  createQuotationChatAttachment,
  createQuotationChatAttachmentWithSessionUid,
  getQuotationRequestChatAttachmentsList,
} from "../../../api/quotation";
import type { SubmitQuotationRequestRQ } from "../../../api/quotation/types";
import { brand } from "../../../context";
import { NotificationBanner } from "../../banners/NotificationBanner";
import { AgentMessage } from "./chat/AgentMessage";
import { AttachmentsList } from "./chat/AttachmentsList";
import { UserMessage } from "./chat/UserMessage";
import { BackButton } from "./componets";
import { Copier } from "./helpers/Copier";
import { QuotationRequestSummary } from "./QuotationRequestSummary";

interface Message {
  chat_session_uid: string;
  id?: string;
  content: string;
  role: "user" | "assistant";
  timestamp: Date;
}

async function SSEChatClient(
  sourceEntity: string,
  userName: string,
  userEmail: string,
  chatSessionUid: string,
  inputValue: string,
  setMessage: (message: string, chatSessionUid: string) => void,
  setSSEDone: () => void,
) {
  let response: Response | null = null;
  try {
    response = await createQuotationChat({
      message: inputValue,
      chat_session_uid: chatSessionUid,
      source_entity: sourceEntity,
      username: userName,
      user_email: userEmail,
    });
  } catch {
    setSSEDone();
    return;
  }

  if (!response) {
    setSSEDone();
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    setSSEDone();
    return;
  }

  const decoder = new TextDecoder("utf-8");

  while (true) {
    try {
      const { value, done } = await reader.read();

      if (done) {
        setSSEDone();
        break;
      }

      const text = decoder.decode(value, { stream: true });
      const textArray = text.split("\n");
      for (const line of textArray) {
        if (line.trim() === "") continue;
        if (line.startsWith("data:") || line.startsWith("event:")) continue;
        try {
          const json = JSON.parse(line);
          setMessage(json.content, json.chat_session_uid);
        } catch {
          setSSEDone();
          break;
        }
      }
    } catch {
      setSSEDone();
      break;
    }
  }
}

type QuotationRequestChatProps = {
  sourceEntity: string;
  targetEntity: string;
  userName: string;
  userEmail: string;
  isSubmitting: boolean;
  onBack: () => void;
  finalizeQuotationRequest: (
    sessionId: string,
    chatSessionUid: string,
    extraComments: string,
    sourceEntity: string,
    targetEntity: string,
    quotationTitle: string,
    dueDate: string,
  ) => void;
};

export function QuotationRequestChat({
  sourceEntity,
  targetEntity,
  userName,
  userEmail,
  isSubmitting,
  onBack,
  finalizeQuotationRequest,
}: QuotationRequestChatProps) {
  const [chatSessionUid, setChatSessionUid] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<"overview" | "quotation_request" | "chat">("chat");

  const messagesRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollToBottom = useCallback(() => {
    messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight, behavior: "smooth" });
  }, []);

  const totalMessageContentLength = useMemo(() => {
    return messages.reduce((acc, message) => acc + (message?.content ?? "").length, 0);
  }, [messages]);

  useEffect(() => {
    if (totalMessageContentLength === 0) {
      return;
    }
    scrollToBottom();
  }, [scrollToBottom, totalMessageContentLength]);

  async function handleSubmit(
    e: React.FormEvent | React.MouseEvent<HTMLButtonElement>,
    specialInstruction?: string,
  ) {
    e.preventDefault();
    if (!inputValue.trim() && !specialInstruction) return;

    let content = inputValue;
    if (specialInstruction) {
      content = specialInstruction;
    }

    const userMessage: Message = {
      chat_session_uid: chatSessionUid,
      id: Date.now().toString(),
      content: inputValue,
      role: "user",
      timestamp: new Date(),
    };
    const placeHolderMessage: Message = {
      chat_session_uid: chatSessionUid,
      id: Date.now().toString(),
      content: "",
      role: "assistant",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage, placeHolderMessage]);

    setIsLoading(true);
    setInputValue("");

    await SSEChatClient(
      sourceEntity,
      userName,
      userEmail,
      chatSessionUid,
      content,
      (message: string, chatSessionUid: string) => {
        setChatSessionUid(chatSessionUid);

        setMessages((prev) => {
          const newMessages = _.cloneDeep(prev);
          const lastMessage = _.last(newMessages);
          if (lastMessage) {
            lastMessage.content = message;
            lastMessage.chat_session_uid = chatSessionUid;
          }
          return newMessages;
        });
      },
      () => {
        setIsLoading(false);
      },
    );
  }

  const uploadFileMutation = useMutation({
    mutationFn: async ({
      chat_session_uid,
      file,
      source_entity,
      username,
      user_email,
    }: {
      chat_session_uid: string;
      file: File;
      source_entity: string;
      username: string;
      user_email: string;
    }) => {
      return createQuotationChatAttachment({
        payload: { chat_session_uid, file, source_entity, username, user_email },
      });
    },
  });

  const uploadFileWithSessionUidMutation = useMutation({
    mutationFn: async ({ chat_session_uid, file }: { chat_session_uid: string; file: File }) => {
      return createQuotationChatAttachmentWithSessionUid({ chat_session_uid, file });
    },
  });

  const { data: chatAttachmentsList, refetch } = useQuery({
    queryKey: ["quotation-request-chat-attachments-list", chatSessionUid],
    queryFn: () => getQuotationRequestChatAttachmentsList({ chat_session_uid: chatSessionUid }),
    enabled: !!chatSessionUid,
    refetchOnWindowFocus: false,
  });

  async function handleUploadFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];

    if (!file) return;
    if (chatSessionUid) {
      uploadFileWithSessionUidMutation.mutate({ chat_session_uid: chatSessionUid, file });
    } else {
      uploadFileMutation.mutate({
        chat_session_uid: chatSessionUid,
        file,
        source_entity: sourceEntity,
        username: userName,
        user_email: userEmail,
      });
    }
  }

  const uploadFileIsLoading = useMemo(() => {
    return uploadFileMutation.isLoading || uploadFileWithSessionUidMutation.isLoading;
  }, [uploadFileMutation.isLoading, uploadFileWithSessionUidMutation.isLoading]);

  const handleUploadReset = useCallback(() => {
    uploadFileMutation.reset();
    uploadFileWithSessionUidMutation.reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [uploadFileMutation, uploadFileWithSessionUidMutation]);

  // Handle the success when uploading the file
  useEffect(() => {
    if (uploadFileMutation.isSuccess) {
      if (!uploadFileMutation.data?.data?.session_uid) return;

      toast.success("File uploaded successfully.");
      setChatSessionUid(uploadFileMutation.data.data.session_uid);
      handleUploadReset();
    }
  }, [uploadFileMutation.isSuccess, uploadFileMutation.data, handleUploadReset]);

  // Handle the error when uploading the file
  useEffect(() => {
    if (uploadFileMutation.isError) {
      let errorMessage = (uploadFileMutation.error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      if (!errorMessage) errorMessage = "Failed to upload file.";
      toast.error(errorMessage);
      handleUploadReset();
    }
  }, [uploadFileMutation.isError, handleUploadReset, uploadFileMutation.error]);

  // Handle the success when uploading the file with session uid
  useEffect(() => {
    if (uploadFileWithSessionUidMutation.isSuccess) {
      toast.success("File uploaded successfully.");
      refetch();
      handleUploadReset();
    }
  }, [uploadFileWithSessionUidMutation.isSuccess, refetch, handleUploadReset]);

  // Handle the error when uploading the file with session uid
  useEffect(() => {
    if (uploadFileWithSessionUidMutation.isError) {
      let errorMessage = (
        uploadFileWithSessionUidMutation?.error as { response: { data: { detail: string } } }
      ).response.data.detail;

      if (!errorMessage) errorMessage = "Failed to upload file.";
      toast.error(errorMessage);

      handleUploadReset();
    }
  }, [
    uploadFileWithSessionUidMutation.isError,
    uploadFileWithSessionUidMutation.error,
    handleUploadReset,
  ]);

  // const {
  //   // sessionId,
  //   // sessionStatus,
  //   // createQuotationMutation,
  //   // quotationRequestData,
  //   // extraCommentsRef,
  //   // quotationSession,
  //   // setQuotationSession,
  // } = useQuotationRequest();

  const attachmentsList = chatAttachmentsList?.data?.attachments ?? [];
  const hasAttachments = useMemo(() => {
    return (attachmentsList?.length ?? 0) > 0;
  }, [attachmentsList?.length]);

  const summaryFormRef = useRef<HTMLFormElement>(null);

  return (
    <Fragment>
      <div
        className={`Modal__card__body overflow-hidden grid pb-0! ${mode === "quotation_request" ? "grid-rows-1" : "grid-rows-[auto_1fr]"}`}
      >
        {mode === "chat" && (
          <NotificationBanner
            variant="info"
            title="New upload functionality has just been released for MIKI Plus AI Agent. You can now upload files to the chat to help the agent understand your request better."
            description={
              <p>
                Use the upload button on the left side of the chat to upload your trip related
                document. Once a document is uploaded just ask the agent to create a trip for you
                based on the document.
                <br />
                For example, once you upload a document you can ask the agent to:
                <ul>
                  <li className="font-bold">
                    Please create a trip for me based on the document. Please make sure to show a
                    day by day itinerary.
                    <Copier content="Please create a trip for me based on the document. Please make sure to show a day by day itinerary." />
                  </li>
                </ul>
              </p>
            }
          />
        )}
        {mode === "overview" && (
          <QuotationRequestSummary
            displayOnly={false}
            sourceEntity={sourceEntity}
            targetEntity={targetEntity}
            message={messages[messages.length - 1]}
            isAgentThinking={isLoading}
            isSubmitting={isSubmitting}
            chatSessionUid={chatSessionUid}
            summaryFormRef={summaryFormRef}
            attachmentsList={attachmentsList}
            onGenerateQuotation={(payload: SubmitQuotationRequestRQ) => {
              finalizeQuotationRequest(
                "",
                chatSessionUid,
                payload.extra_comments,
                payload.source_entity,
                payload.target_entity,
                payload.quotation_title,
                payload.due_date,
              );
            }}
          />
        )}
        {mode === "chat" && (
          <div className="grid gap-3 grid-rows-[1fr_auto] h-full overflow-hidden">
            <div
              className={`grid gap-3 ${hasAttachments ? "grid-cols-[1fr_3fr]" : "grid-cols-[1fr]"} overflow-hidden`}
            >
              {hasAttachments && (
                <AttachmentsList
                  attachments={chatAttachmentsList?.data?.attachments || []}
                  chatSessionUid={chatSessionUid}
                  refetchAttachments={() => refetch()}
                />
              )}
              <div
                className="overflow-y-auto p-4 space-y-4 border border-gray-200 rounded-md"
                ref={messagesRef}
              >
                {messages.map((message, idx) => {
                  return (
                    <div key={`${idx}-${message.id}`}>
                      {message.role === "user" ? (
                        <UserMessage message={message} />
                      ) : (
                        <AgentMessage message={message} isGlobalLoading={isLoading} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <form onSubmit={handleSubmit} className="rounded-md">
              <div className="grid grid-cols-[auto_1fr_auto] gap-2">
                <input
                  type="file"
                  onChange={handleUploadFile}
                  className="hidden"
                  disabled={isLoading || uploadFileIsLoading}
                  accept={CHAT_ATTACHMENT_MIME_TYPES.join(",")}
                  ref={fileInputRef}
                />
                <button
                  type="button"
                  className="rounded-full! border-gray-200 border-2! p-3 hover:bg-gray-400 hover:[&_svg]:text-white transition-all"
                  title="Upload file"
                  disabled={isLoading || uploadFileIsLoading}
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current?.click();
                    }
                  }}
                >
                  {uploadFileIsLoading ? (
                    <InlineIcon icon="mingcute:loading-line" className="w-5 h-5 animate-spin" />
                  ) : (
                    <InlineIcon icon="mdi:paperclip" className="w-5 h-5" />
                  )}
                </button>
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Type your trip request..."
                  className="p-2 border border-gray-200 rounded-md focus:outline-none"
                  disabled={isLoading || uploadFileIsLoading}
                />
                <button
                  type="submit"
                  className="rounded-full! border-gray-200 border-2! p-3 hover:bg-gray-400 hover:[&_svg]:text-white transition-all"
                  onClick={handleSubmit}
                  disabled={isLoading || uploadFileIsLoading}
                >
                  <InlineIcon icon="mdi:arrow-up" className="w-5 h-5" />
                </button>
              </div>
            </form>
          </div>
        )}
        {/* {mode === "quotation_request" && (
          <div className="grid p-3 h-full overflow-hidden grid-cols-[1fr_3fr]">
            <QuotationRequestSummary
              displayOnly={true}
              sourceEntity={sourceEntity}
              targetEntity={targetEntity}
              message={messages[messages.length - 1]}
              isGlobalLoading={isLoading}
              onGenerateQuotation={(payload: QuotationRequestRQ) => {
                createQuotationMutation.mutate({ payload });
                setMode("quotation_request");
              }}
            />
            <QuotationRequestDetails
              generationProgress={quotationRequestData?.data?.generation_progress ?? 0}
              generationProgressMessage={
                quotationRequestData?.data?.generation_progress_message ?? ""
              }
              sessionStatus={sessionStatus}
              sessionId={sessionId}
              extraCommentsRef={extraCommentsRef}
              quotationSession={quotationSession ?? null}
              setQuotationSession={
                setQuotationSession as (
                  p: (session: QuotationRequestSessionType) => QuotationRequestSessionType,
                ) => void
              }
            />
          </div>
        )} */}
      </div>
      <div className="Modal__card__actions justify-end">
        {mode === "chat" && <BackButton onBack={onBack} />}
        {mode !== "chat" && (
          <button
            className="Button"
            data-brand={brand}
            disabled={isLoading}
            onClick={() => {
              setMessages((prev) => _.cloneDeep(prev).slice(0, -2));
              setMode("chat");
            }}
            type="button"
          >
            Back
          </button>
        )}
        {mode === "chat" &&
          messages.length > 1 &&
          messages[messages.length - 1].role === "assistant" &&
          messages[messages.length - 1].content.trim() !== "" && (
            <button
              type="button"
              className="Button"
              data-brand={brand}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                setInputValue("");
                handleSubmit(e, "<--- Generate Final Quotation Request --->");
                setMode("overview");
              }}
            >
              View Quotation Request Summary
            </button>
          )}
        {mode === "overview" && (
          <button
            type="button"
            className="Button"
            data-brand={brand}
            disabled={isLoading}
            onClick={() => {
              if (summaryFormRef.current) {
                summaryFormRef.current.requestSubmit();
              }
            }}
          >
            Generate Quotation Request
          </button>
        )}
        {/* {mode === "quotation_request" && quotationRequestData?.data.status === "COMPLETED" && (
          <button
            className="Button"
            type="button"
            data-brand={brand}
            onClick={() => {
              let extraComments = "";
              if (!!extraCommentsRef.current) {
                extraComments = extraCommentsRef.current.value;
              }

              finalizeQuotationRequest(sessionId, chatSessionUid, extraComments);
            }}
          >
            Submit Quotation Request
          </button>
        )} */}
      </div>
    </Fragment>
  );
}
